import * as angular from 'angular';

import { FlamingoTypes } from '@mode/shared/contract-common';
import { FlamingoResponse } from '@mode/shared/contract-common';
import { testClipboardSupport } from '@mode/shared/util-js';
import { prepareDataForCopying, stringifyLargeNumbers, textHasLargeNumber } from '@mode/shared/util-js';
import { ToastService } from '@mode/shared/util-toast';
import { MAX_COPY_BYTES, MAX_COPY_COMPAT } from '../../common/constants';
import { formatBytes } from '../../common/format.bytes.helper';

export const ID = 'copyDatasetButton';

class CopyDatasetButtonController implements angular.IController {
  static $inject = ['$window', '$scope', 'ToastService'];

  private jsonToCopy: string;
  private error?: FlamingoResponse.FlamingoErrorResponse;
  private csvToDownload: string;

  downloading: boolean;
  copyTooltipHtml: string;
  copyButtonHtml: string;
  columns: FlamingoTypes.ResultColumns;
  largeDatasetForCopy: string;
  isLargeClipboardEnabled: boolean;
  isClipboardEnabled: boolean;
  datasetSizeBytes: number;
  sizeLimitText: string;
  copying: boolean;
  linkToDownload: string;

  constructor(
    private $window: angular.IWindowService,
    private $scope: angular.IScope,
    private alertService: ToastService
  ) {}

  $onInit() {
    // check here instead of using $scope.$watch on the copy-to-clipboard clipboard-upgraded attribute
    this.isLargeClipboardEnabled = testClipboardSupport(this.$window);

    this.copyButtonHtml = 'Copy';
    this.downloading = false;

    this.sizeLimitText = formatBytes(this.sizeLimit);
    this.setCopyTooltip();
  }

  $onChanges(changed: angular.IOnChangesObject) {
    if (changed.datasetSizeBytes) {
      this.setCopyTooltip();
    }

    if (changed.jsonToCopy) {
      this.largeDatasetForCopy = undefined;
      this.copyButtonHtml = 'Copy';
      this.setCopyTooltip();
    }
  }

  onFeatureDetect(upgraded: boolean, legacy: boolean) {
    this.isClipboardEnabled = upgraded || legacy;
    this.isLargeClipboardEnabled = upgraded;
    this.setCopyTooltip();
  }

  copySuccess() {
    this.copying = false;
    this.alertService.success('Copied to clipboard');
  }

  copyFailed() {
    this.copying = false;
    this.alertService.success('Error copying to clipboard');
  }

  downloadOrCopyDataset($event: angular.IAngularEvent) {
    if (this.largeDatasetForCopy != null) {
      // Pass through to copy-to-clipboard directive
      return;
    }

    if (!this.copyDisabled) {
      this.downloading = true;
      this.copyButtonHtml = '<div class="loading">Generating</div>';

      $.ajax({
        type: 'GET',
        url: this.jsonToCopy,
        dataType: 'json',
        converters: {
          'text json': (result) => {
            if (textHasLargeNumber(result)) {
              return JSON.parse(stringifyLargeNumbers(result));
            }
            return JSON.parse(result);
          },
        },
        success: (data) => {
          this.largeDatasetForCopy = prepareDataForCopying({ content: data, columns: this.columns });
          this.$scope.$evalAsync(() => {
            this.copyButtonHtml = 'Copy to clipboard';
            this.setCopyTooltip();
            this.downloading = false;
          });
        },
        error: () => {
          this.alertService.error('Failed to retrieve data for copying');
        },
      });
    }

    $event.preventDefault();
    $event.stopPropagation();
  }

  setCopyTooltip() {
    if (this.datasetSizeBytes > this.sizeLimit) {
      if (this.isLargeClipboardEnabled) {
        this.copyTooltipHtml = `Dataset is ${formatBytes(this.datasetSizeBytes - this.sizeLimit)} over the ${
          this.sizeLimitText
        } limit for copying`;
      } else {
        this.copyTooltipHtml = `Dataset is ${formatBytes(this.datasetSizeBytes - this.sizeLimit)} over the ${
          this.sizeLimitText
        } limit <br />
          for copying. Larger datasets can be <br />
          copied with Chrome or Firefox.`;
      }
    } else if (this.largeDatasetForCopy != null) {
      this.copyTooltipHtml = 'Copy all rows to clipboard';
    } else if (this.copying) {
      this.copyTooltipHtml = '';
    } else if (this.isClipboardEnabled) {
      this.copyTooltipHtml = `Generate results (Up to ${this.sizeLimitText})`;
    } else {
      this.copyTooltipHtml = 'Your browser does not support automated clipboard functionality';
    }
  }

  get sizeLimit() {
    if (!this.isLargeClipboardEnabled) {
      return MAX_COPY_COMPAT;
    } else {
      return MAX_COPY_BYTES;
    }
  }

  get copyDisabled(): boolean {
    return (
      this.copying ||
      this.downloading ||
      !this.isClipboardEnabled ||
      this.datasetSizeBytes > this.sizeLimit ||
      !this.linkToDownload == null
    );
  }
}

export const copyDatasetButton: angular.IComponentOptions = {
  bindings: {
    queryToken: '<',
    rowsCount: '<',
    datasetSizeBytes: '<',
    csvToDownload: '<',
    jsonToCopy: '<',
    columns: '<',
    oversized: '<',
    error: '<?',
    showViewLinks: '<?',
  },
  template: `
    <div
      uib-tooltip-html="ctrl.copyTooltipHtml"
      tooltip-append-to-body="true"
      tooltip-popup-delay="500"
      tooltip-placement="top-right"
      >
      <button
        class="query-run-action btn btn-default copy-button"
        id="copy-to-clipboard"

        copy-to-clipboard
        clipboard-detect="ctrl.onFeatureDetect($upgraded, $legacy)"
        clipboard-success="ctrl.copySuccess()"
        clipboard-failure="ctrl.copyFailed()"
        clipboard-text="ctrl.largeDatasetForCopy"

        ng-disabled="ctrl.copyDisabled"
        ng-bind-html="ctrl.copyButtonHtml"
        ng-click="ctrl.downloadOrCopyDataset($event)">
      </button>
    </div>
  `,
  controller: CopyDatasetButtonController,
  controllerAs: 'ctrl',
};
